import * as React from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
} from '@floating-ui/react';
import { Check, Circle } from 'lucide-react';

import { cn } from '../../services/utils';

// Context for dropdown state management
interface DropdownContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  refs: ReturnType<typeof useFloating>['refs'];
  floatingStyles: ReturnType<typeof useFloating>['floatingStyles'];
  context: ReturnType<typeof useFloating>['context'];
  getFloatingProps: ReturnType<typeof useInteractions>['getFloatingProps'];
  getReferenceProps: ReturnType<typeof useInteractions>['getReferenceProps'];
  align: 'start' | 'center' | 'end';
  sideOffset: number;
  setAlign: (align: 'start' | 'center' | 'end') => void;
  setSideOffset: (offset: number) => void;
}

const DropdownContext = React.createContext<DropdownContextValue | null>(null);

const useDropdownContext = () => {
  const context = React.useContext(DropdownContext);
  if (!context) {
    throw new Error('Dropdown components must be used within DropdownMenu');
  }
  return context;
};

// Root component
const DropdownMenu = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
  }
>(({ children, open: controlledOpen, onOpenChange, ...props }, ref) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const [align, setAlign] = React.useState<'start' | 'center' | 'end'>('center');
  const [sideOffset, setSideOffset] = React.useState(4);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;

  // Map align to placement
  const placement = React.useMemo(() => {
    switch (align) {
      case 'start':
        return 'bottom-start';
      case 'end':
        return 'bottom-end';
      default:
        return 'bottom';
    }
  }, [align]);

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    middleware: [offset(sideOffset), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'menu' });

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  return (
    <DropdownContext.Provider
      value={{
        open,
        setOpen,
        refs,
        floatingStyles,
        context,
        getReferenceProps,
        getFloatingProps,
        align,
        sideOffset,
        setAlign,
        setSideOffset,
      }}
    >
      <div ref={ref} {...props}>
        {children}
      </div>
    </DropdownContext.Provider>
  );
});
DropdownMenu.displayName = 'DropdownMenu';

// Trigger component
const DropdownMenuTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    asChild?: boolean;
  }
>(({ children, asChild = false, ...props }, ref) => {
  const { open, refs, getReferenceProps } = useDropdownContext();

  if (asChild && React.isValidElement(children)) {
    // Pass consumer props to getReferenceProps for proper event handler composition
    const childProps = (children.props ?? {}) as React.HTMLAttributes<HTMLElement>;
    const referenceProps = getReferenceProps({
      ...childProps,
      ...props,
      'aria-haspopup': 'menu',
      'aria-expanded': open,
    });

    return React.cloneElement(children, {
      ...childProps,
      ...referenceProps,
      ref: (node: HTMLElement) => {
        refs.setReference(node);
        if (typeof ref === 'function') {
          ref(node as HTMLButtonElement);
        } else if (ref) {
          ref.current = node as HTMLButtonElement;
        }
        // Handle original ref if it exists
        const originalRef = (children as any).ref;
        if (typeof originalRef === 'function') {
          originalRef(node);
        } else if (originalRef) {
          originalRef.current = node;
        }
      },
    });
  }

  return (
    <button
      type="button"
      ref={(node) => {
        refs.setReference(node);
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      }}
      {...getReferenceProps({
        ...props,
        'aria-haspopup': 'menu',
        'aria-expanded': open,
      })}
    >
      {children}
    </button>
  );
});
DropdownMenuTrigger.displayName = 'DropdownMenuTrigger';

// Content component
const DropdownMenuContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    align?: 'start' | 'center' | 'end';
    sideOffset?: number;
  }
>(({ className, align = 'center', sideOffset = 4, ...props }, ref) => {
  const { open, refs, floatingStyles, getFloatingProps, setAlign, setSideOffset } =
    useDropdownContext();

  // Update context with props when component mounts or props change
  React.useEffect(() => {
    setAlign(align);
    setSideOffset(sideOffset);
  }, [align, sideOffset, setAlign, setSideOffset]);

  if (!open) return null;

  return (
    <div
      ref={(node) => {
        refs.setFloating(node);
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      }}
      style={floatingStyles}
      className={cn(
        'z-[99999] min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md',
        className
      )}
      {...getFloatingProps(props)}
    />
  );
});
DropdownMenuContent.displayName = 'DropdownMenuContent';

// Menu Item component
const DropdownMenuItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    inset?: boolean;
    disabled?: boolean;
  }
>(({ className, inset, disabled, onClick, onKeyDown, ...props }, forwardedRef) => {
  const { setOpen } = useDropdownContext();
  const itemRef = React.useRef<HTMLDivElement>(null);

  // Merge refs - return null if not yet mounted
  React.useImperativeHandle(forwardedRef, () => itemRef.current || (null as any));

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!disabled) {
      onClick?.(e);
      setOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Call original onKeyDown handler first
    onKeyDown?.(e);

    if (e.defaultPrevented) {
      return;
    }

    if (!disabled && (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar')) {
      // Prevent default for Space to avoid page scroll
      if (e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
      }

      // Trigger real click on the element
      itemRef.current?.click();
      setOpen(false);
    }
  };

  return (
    <div
      ref={itemRef}
      className={cn(
        'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground',
        inset && 'pl-8',
        disabled && 'pointer-events-none opacity-50',
        className
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="menuitem"
      tabIndex={disabled ? -1 : 0}
      {...props}
    />
  );
});
DropdownMenuItem.displayName = 'DropdownMenuItem';

// Checkbox Item component
const DropdownMenuCheckboxItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    checked?: boolean;
    disabled?: boolean;
  }
>(({ className, children, checked, disabled, onClick, onKeyDown, ...props }, forwardedRef) => {
  const itemRef = React.useRef<HTMLDivElement>(null);

  // Merge refs
  React.useImperativeHandle(forwardedRef, () => itemRef.current!);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!disabled) {
      onClick?.(e);
      // Don't auto-close for checkbox items
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Call original onKeyDown handler first
    onKeyDown?.(e);

    if (e.defaultPrevented) {
      return;
    }

    if (!disabled && (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar')) {
      // Prevent default for Space to avoid page scroll
      if (e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
      }

      // Trigger real click on the element
      itemRef.current?.click();
    }
  };

  return (
    <div
      ref={itemRef}
      className={cn(
        'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground',
        disabled && 'pointer-events-none opacity-50',
        className
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="menuitemcheckbox"
      aria-checked={checked}
      tabIndex={disabled ? -1 : 0}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        {checked && <Check className="h-4 w-4" />}
      </span>
      {children}
    </div>
  );
});
DropdownMenuCheckboxItem.displayName = 'DropdownMenuCheckboxItem';

// Radio Item component
const DropdownMenuRadioItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    value?: string;
    checked?: boolean;
    disabled?: boolean;
  }
>(({ className, children, checked, disabled, onClick, onKeyDown, ...props }, forwardedRef) => {
  const itemRef = React.useRef<HTMLDivElement>(null);

  // Merge refs
  React.useImperativeHandle(forwardedRef, () => itemRef.current!);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!disabled) {
      onClick?.(e);
      // Don't auto-close for radio items
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Call original onKeyDown handler first
    onKeyDown?.(e);

    if (e.defaultPrevented) {
      return;
    }

    if (!disabled && (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar')) {
      // Prevent default for Space to avoid page scroll
      if (e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
      }

      // Trigger real click on the element
      itemRef.current?.click();
    }
  };

  return (
    <div
      ref={itemRef}
      className={cn(
        'relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground',
        disabled && 'pointer-events-none opacity-50',
        className
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="menuitemradio"
      aria-checked={checked}
      tabIndex={disabled ? -1 : 0}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        {checked && <Circle className="h-2 w-2 fill-current" />}
      </span>
      {children}
    </div>
  );
});
DropdownMenuRadioItem.displayName = 'DropdownMenuRadioItem';

// Label component
const DropdownMenuLabel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('px-2 py-1.5 text-sm font-semibold', inset && 'pl-8', className)}
    {...props}
  />
));
DropdownMenuLabel.displayName = 'DropdownMenuLabel';

// Separator component
const DropdownMenuSeparator = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-muted', className)}
    role="separator"
    {...props}
  />
));
DropdownMenuSeparator.displayName = 'DropdownMenuSeparator';

// Shortcut component
const DropdownMenuShortcut = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement>
>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn('ml-auto text-xs tracking-widest opacity-60', className)}
    {...props}
  />
));
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';

// Group components (simple containers)
const DropdownMenuGroup = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ ...props }, ref) => <div ref={ref} {...props} />
);
DropdownMenuGroup.displayName = 'DropdownMenuGroup';

const DropdownMenuPortal = ({ children }: { children: React.ReactNode }) => {
  // We don't need a portal in our implementation, but keep for API compatibility
  return <>{children}</>;
};
DropdownMenuPortal.displayName = 'DropdownMenuPortal';

const DropdownMenuRadioGroup = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ ...props }, ref) => <div ref={ref} {...props} />);
DropdownMenuRadioGroup.displayName = 'DropdownMenuRadioGroup';

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
};
